import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { BLOG, IMAGES } from "@/data/site";
import { usePageMeta, PageHero, Breadcrumb, PrimaryButton, CTABanner } from "@/components/common";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG.find((b) => b.slug === slug);
  const idx = BLOG.findIndex((b) => b.slug === slug);
  usePageMeta(post ? post.title : "Article", post ? post.excerpt : "");
  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG.filter((b) => b.slug !== slug).slice(0, 3);

  return (
    <div data-testid="blog-post-page">
      <PageHero label={post.category} title={post.title} image={post.image || IMAGES.texture} />
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Blog", to: "/blog" }, { label: post.title }]} />

      <article className="py-20 bg-adn-white">
        <div className="max-w-3xl mx-auto px-6 text-lg text-zinc-700 leading-relaxed space-y-6">
          <p className="text-xl text-adn-charcoal font-medium">{post.excerpt}</p>
          <p>At A.D.N Stucco Inc, we believe an informed homeowner makes the best decisions for their property. This guide breaks down what you need to know about <strong>{post.title.toLowerCase().replace(/\?$/, "")}</strong> and how it applies to homes and businesses across Central Florida.</p>

          <h2 className="text-2xl font-heading font-bold text-adn-charcoal pt-4">Why it matters</h2>
          <p>Stucco is one of the most durable and attractive exterior finishes available, but it performs best when properly installed and maintained. Understanding the fundamentals helps you protect your investment, avoid costly damage, and keep your property looking its best in Florida's demanding climate.</p>

          <h2 className="text-2xl font-heading font-bold text-adn-charcoal pt-4">Key takeaways</h2>
          <ul className="space-y-3">
            {["Quality materials and expert application make all the difference in longevity.", "Florida's heat, humidity, and rain require weather-resistant finishes and proper waterproofing.", "Regular inspections and prompt repairs prevent small issues from becoming major problems.", "Working with a licensed and insured contractor protects your home and your budget."].map((t) => (
              <li key={t} className="flex items-start gap-3"><Check size={20} className="text-adn-red shrink-0 mt-1" /><span>{t}</span></li>
            ))}
          </ul>

          <h2 className="text-2xl font-heading font-bold text-adn-charcoal pt-4">Our recommendation</h2>
          <p>Whether you're planning a new installation, noticing cracks, or simply want a professional assessment, the best first step is a free estimate. Our experienced team will inspect your property, explain your options in plain language, and provide honest pricing with no obligation.</p>

          <div className="bg-adn-off border-l-4 border-adn-red p-6 not-prose">
            <p className="text-adn-charcoal font-medium m-0">Have questions about your specific project? A.D.N Stucco Inc offers free estimates throughout Central Florida.</p>
            <div className="mt-4"><PrimaryButton to="/contact" testid="blogpost-cta">Request a Free Estimate</PrimaryButton></div>
          </div>
        </div>
      </article>

      <section className="py-16 bg-adn-off">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-heading font-extrabold mb-8">Related articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((b) => (
              <Link key={b.slug} to={`/blog/${b.slug}`} className="group bg-white border border-zinc-200 p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <span className="label-accent text-adn-red">{b.category}</span>
                <h3 className="text-lg font-heading font-bold mt-3 leading-snug">{b.title}</h3>
                <span className="text-adn-red text-sm font-semibold mt-4 flex items-center gap-2">Read <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTABanner />
    </div>
  );
}
